import React from 'react';
import moment from 'moment-timezone';
import { useNavigate } from 'react-router-dom';
import classes from "./MatchLiveLayoutNew.module.css";
import { useTranslation } from 'react-i18next';

const FT_STATUSES   = ["FT", "AET", "PEN"];
const LIVE_STATUSES = ["1H", "2H", "ET", "P", "HT", "LIVE"];

const MatchLiveLayoutNew = ({ match_details, match, index }) => {
  const navigate = useNavigate();
  const {t}=useTranslation();

  const short   = match_details?.fixture?.status?.short;
  const elapsed = match_details?.fixture?.status?.elapsed;
  const extra   = match_details?.fixture?.status?.extra;

  const isFt    = FT_STATUSES.includes(short);
  const isLive  = LIVE_STATUSES.includes(short);
  const showScore = isFt || isLive;

  const homeGoals = match_details?.goals?.home ?? 0;
  const awayGoals = match_details?.goals?.away ?? 0;
  const homeWin = homeGoals > awayGoals;
  const awayWin = awayGoals > homeGoals;

  const getStatusLabel = () => {
    if (isFt)                             return { label: t("100"),             live: false };
    if (short === "SUSP")                 return { label: t("101"),             live: false };
    if (short === "PST")                  return { label: t("102"),             live: false };
    if (short === "CANC")                 return { label: t("103"),             live: false };
    if (["ABD","AWD","WO"].includes(short)) return { label: t("104"),           live: false };
    if (short === "HT")                   return { label: `${t("105")} ${elapsed}'`, live: true  };
    if (short === "ET")                   return { label: `${elapsed}' ${t("106")} +${extra}'`, live: true };
    if (short === "P")                    return { label: `${t("107")} ${elapsed}'`, live: true  };
    if (isLive)                           return { label: `${elapsed}'`,           live: true  };
    return null;
  };

  const status = getStatusLabel();

  return (
    <div
      className={`${classes.card} ${isLive ? classes.cardLive : ""}`}
      style={{ animationDelay: `${index * 0.07}s` }}
    >
      {/* Pulsing live border glow — only shown for live matches */}
      {isLive && <span className={classes.liveBorderGlow} />}

      {/* ── League + status header ── */}
      <div className={classes.cardHeader}>
        <div className={classes.leagueLeft}>
          <span className={classes.leagueBadge}>🏆</span>
          <span className={classes.leagueName}>{match?.league_name}</span>
        </div>
        {status && (
          status.live ? (
            <span className={classes.statusPillLive}>
              <span className={classes.statusDot} />
              {status.label}
            </span>
          ) : (
            <span className={classes.statusPillFt}>{status.label}</span>
          )
        )}
      </div>

      {/* ── Teams + score ── */}
      <div className={classes.teamsRow}>

        {/* Home */}
        <div className={`${classes.team} ${homeWin && showScore ? classes.teamWinner : ""}`}>
          <div className={`${classes.logoWrap} ${homeWin && showScore ? classes.logoWrapWinner : ""}`}>
            <img src={match?.home_team_image} alt={match?.home_team_name} className={classes.teamLogo} />
          </div>
          <p className={classes.teamName}>{match?.home_team_name}</p>
          {homeWin && showScore && <span className={classes.winnerTag}>{t("108")}</span>}
        </div>

        {/* Score / VS */}
        <div className={classes.scoreBlock}>
          {showScore ? (
            <>
              <div className={classes.scoreRow}>
                <span className={`${classes.score} ${homeWin ? classes.scoreWin : awayWin ? classes.scoreLose : ""}`}>
                  {homeGoals}
                </span>
                <span className={classes.scoreDash}>—</span>
                <span className={`${classes.score} ${awayWin ? classes.scoreWin : homeWin ? classes.scoreLose : ""}`}>
                  {awayGoals}
                </span>
              </div>
              {isFt && <span className={classes.ftLabel}>{t("109")}</span>}
              {isLive && (
                <span className={classes.liveMinute}>
                  <span className={classes.liveMinuteDot} />
                  {elapsed}'
                </span>
              )}
            </>
          ) : (
            <>
              <span className={classes.vsText}>{t("110")}</span>
              <p className={classes.matchDate}>
                {moment.utc(match?.match_date_time).local().format("ddd DD MMM")}
              </p>
              <p className={classes.matchTime}>
                {moment.utc(match?.match_date_time).local().format("hh:mm A")}
              </p>
            </>
          )}
        </div>

        {/* Away */}
        <div className={`${classes.team} ${awayWin && showScore ? classes.teamWinner : ""}`}>
          <div className={`${classes.logoWrap} ${awayWin && showScore ? classes.logoWrapWinner : ""}`}>
            <img src={match?.away_team_image} alt={match?.away_team_name} className={classes.teamLogo} />
          </div>
          <p className={classes.teamName}>{match?.away_team_name}</p>
          {awayWin && showScore && <span className={classes.winnerTag}>{t("111")}</span>}
        </div>

      </div>

      {/* ── Match stats strip (live only) ── */}
      {isLive && (
        <div className={classes.statsStrip}>
          <div className={classes.statItem}>
            <span className={classes.statNum}>{match_details?.statistics?.[0]?.statistics?.find(s => s.type === "Shots on Goal")?.value ?? 0}</span>
            <span className={classes.statLabel}>{t("112")}</span>
            <span className={classes.statNum}>{match_details?.statistics?.[1]?.statistics?.find(s => s.type === "Shots on Goal")?.value ?? 0}</span>
          </div>
          <div className={classes.statDivider} />
          <div className={classes.statItem}>
            <span className={classes.statNum}>{match_details?.statistics?.[0]?.statistics?.find(s => s.type === "Ball Possession")?.value ?? "—"}</span>
            <span className={classes.statLabel}>{t("113")}</span>
            <span className={classes.statNum}>{match_details?.statistics?.[1]?.statistics?.find(s => s.type === "Ball Possession")?.value ?? "—"}</span>
          </div>
          <div className={classes.statDivider} />
          <div className={classes.statItem}>
            <span className={classes.statNum}>{match_details?.statistics?.[0]?.statistics?.find(s => s.type === "Yellow Cards")?.value ?? 0}</span>
            <span className={classes.statLabel}>{t("114")}</span>
            <span className={classes.statNum}>{match_details?.statistics?.[1]?.statistics?.find(s => s.type === "Yellow Cards")?.value ?? 0}</span>
          </div>
        </div>
      )}

      {/* ── View Details CTA ── */}
      <div className={classes.cardFooter}>
        <button
          type="button"
          className={classes.btnDetails}
          onClick={() => navigate("/match-details", { state: { match_fixture_id: match?.match_fixture_id } })}
        >
          <span className={classes.btnGlow} />
          <span className={classes.btnIcon}>📊</span>
          <span className={classes.btnText}>{t("115")}</span>
          <span className={classes.btnArrow}>→</span>
        </button>
      </div>

    </div>
  );
};

export default MatchLiveLayoutNew;